export interface WordPressPost {
  id: number;
  slug: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  date: string;
  date_gmt: string;
  author: number;
  categories: number[];
  tags: number[];
  featured_media: number;
  uagb_featured_image_src: {
    full: [string, number, number, boolean];
    medium: [string, number, number, boolean];
    thumbnail: [string, number, number, boolean];
    medium_large: [string, number, number, boolean];
    large: [string, number, number, boolean];
  };
  uagb_author_info: {
    display_name: string;
    author_link: string;
  };
  acf: {
    tags: string;
    author: string;
    publish_date: string;
  };
}

export interface WordPressCategory {
  id: number;
  name: string;
  slug: string;
  count: number;
}

export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  tags: string[];
  content?: string;
}

const API_BASE_URL = 'https://thomass362.sg-host.com/wp-json/wp/v2';

export const calculateReadTime = (content: string): string => {
  const cleanText = content.replace(/<[^>]*>/g, '');
  const wordCount = cleanText.split(/\s+/).length;
  const readTime = Math.ceil(wordCount / 200);
  return `${readTime} min`;
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  };
  return date.toLocaleDateString('es-ES', options);
};

export const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const fetchWordPressPosts = async (): Promise<WordPressPost[]> => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(`${API_BASE_URL}/posts?per_page=100&acf_format=standard&_=${timestamp}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching WordPress posts:', error);
    throw error;
  }
};

export const fetchWordPressCategories = async (): Promise<WordPressCategory[]> => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(`${API_BASE_URL}/categories?_=${timestamp}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching WordPress categories:', error);
    throw error;
  }
};

export const fetchWordPressPost = async (id: string): Promise<WordPressPost> => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(`${API_BASE_URL}/posts/${id}?acf_format=standard&_=${timestamp}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching WordPress post:', error);
    throw error;
  }
};

export const fetchWordPressPostBySlug = async (slug: string): Promise<WordPressPost> => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(`${API_BASE_URL}/posts?slug=${slug}&acf_format=standard&_=${timestamp}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const posts = await response.json();
    if (posts.length === 0) {
      throw new Error('Post not found');
    }
    return posts[0];
  } catch (error) {
    console.error('Error fetching WordPress post by slug:', error);
    throw error;
  }
};

export const transformWordPressPostToBlogPost = (
  wpPost: WordPressPost, 
  categories: WordPressCategory[] = []
): BlogPost => {
  const categoryNames = wpPost.categories
    .map(catId => categories.find(cat => cat.id === catId)?.name)
    .filter(Boolean) as string[];
  
  const tags = wpPost.acf?.tags 
    ? wpPost.acf.tags.split(',').map(tag => tag.trim()).filter(Boolean)
    : [];

  const featuredImage = wpPost.uagb_featured_image_src?.large?.[0] || 
                       wpPost.uagb_featured_image_src?.medium?.[0] ||
                       wpPost.uagb_featured_image_src?.full?.[0] ||
                       'https://images.unsplash.com/photo-1574482620811-1aa16ffe3c82?w=800&h=600&fit=crop';

  const cleanExcerpt = stripHtml(wpPost.excerpt.rendered || wpPost.content.rendered);
  const limitedExcerpt = cleanExcerpt.length > 150 
    ? cleanExcerpt.substring(0, 150) + '...' 
    : cleanExcerpt;

  return {
    id: wpPost.id,
    slug: wpPost.slug,
    title: stripHtml(wpPost.title.rendered),
    excerpt: limitedExcerpt,
    author: wpPost.acf?.author || wpPost.uagb_author_info?.display_name || 'Autor',
    date: wpPost.acf?.publish_date || formatDate(wpPost.date),
    readTime: calculateReadTime(wpPost.content.rendered),
    category: categoryNames[0] || 'General',
    image: featuredImage,
    tags: tags,
    content: wpPost.content.rendered
  };
};